import * as tus from 'tus-js-client';
import { supabase } from './supabase';

export interface UploadResult {
  url: string
  path: string
  error?: string
}

export const storage = {
  async uploadStudioPhoto(file: File, studioId: string): Promise<UploadResult> {
    try {
      // Get file extension from original name
      const originalExt = file.name.split('.').pop() || 'png';
      
      // Create a simple, safe filename with timestamp
      const fileName = `${Date.now()}.${originalExt}`
      const filePath = `${studioId}/${fileName}`

      const { error } = await supabase.storage
        .from('studio-photos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (error) {
        throw error
      }

      const { data: { publicUrl } } = supabase.storage
        .from('studio-photos')
        .getPublicUrl(filePath)

      return {
        url: publicUrl,
        path: filePath
      }
    } catch (error) {
      return {
        url: '',
        path: '',
        error: error instanceof Error ? error.message : 'Failed to upload photo'
      }
    }
  },

  async uploadStudioLogo(file: File, studioId: string): Promise<UploadResult> {
    try {
      const originalExt = file.name.split('.').pop() || 'png';
      const filePath = `${studioId}/logo.${originalExt}`;

      const { error } = await supabase.storage
        .from('studio-photos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('studio-photos')
        .getPublicUrl(filePath);

      return { url: publicUrl, path: filePath };
    } catch (error) {
      return {
        url: '',
        path: '',
        error: error instanceof Error ? error.message : 'Failed to upload logo',
      };
    }
  },

  async uploadRoomPhoto(file: File, roomId: string): Promise<UploadResult> {
    try {
      // Get file extension from original name
      const originalExt = file.name.split('.').pop() || 'png';

      // Create a simple, safe filename with timestamp
      const fileName = `${Date.now()}.${originalExt}`
      const filePath = `${roomId}/${fileName}`

      const { error } = await supabase.storage
        .from('room-photos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (error) {
        throw error
      }

      const { data: { publicUrl } } = supabase.storage
        .from('room-photos')
        .getPublicUrl(filePath)

      return {
        url: publicUrl,
        path: filePath
      }
    } catch (error) {
      return {
        url: '',
        path: '',
        error: error instanceof Error ? error.message : 'Failed to upload photo'
      }
    }
  },

  async uploadClassPhoto(file: File, classId: string): Promise<UploadResult> {
    try {
      // Get file extension from original name
      const originalExt = file.name.split('.').pop() || 'png';

      // Create a simple, safe filename with timestamp
      const fileName = `${Date.now()}.${originalExt}`
      const filePath = `${classId}/${fileName}`

      const { error } = await supabase.storage
        .from('class-photos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (error) {
        throw error
      }

      const { data: { publicUrl } } = supabase.storage
        .from('class-photos')
        .getPublicUrl(filePath)

      return {
        url: publicUrl,
        path: filePath
      }
    } catch (error) {
      return {
        url: '',
        path: '',
        error: error instanceof Error ? error.message : 'Failed to upload photo'
      }
    }
  },

  async uploadEventPhoto(file: File, eventId: string): Promise<UploadResult> {
    try {
      const originalExt = file.name.split('.').pop() || 'png';
      const fileName = `${Date.now()}.${originalExt}`;
      const filePath = `${eventId}/${fileName}`;

      const { error } = await supabase.storage
        .from('event-photos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('event-photos')
        .getPublicUrl(filePath);

      return { url: publicUrl, path: filePath };
    } catch (error) {
      return {
        url: '',
        path: '',
        error: error instanceof Error ? error.message : 'Failed to upload photo',
      };
    }
  },

  async uploadProgramPhoto(file: File, programId: string): Promise<UploadResult> {
    try {
      const originalExt = file.name.split('.').pop() || 'png';
      const fileName = `${Date.now()}.${originalExt}`;
      const filePath = `${programId}/${fileName}`;

      const { error } = await supabase.storage
        .from('program-photos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('program-photos')
        .getPublicUrl(filePath);

      return { url: publicUrl, path: filePath };
    } catch (error) {
      return {
        url: '',
        path: '',
        error: error instanceof Error ? error.message : 'Failed to upload photo',
      };
    }
  },

  async deletePhoto(bucket: 'studio-photos' | 'room-photos' | 'class-photos' | 'event-photos' | 'program-photos', path: string): Promise<boolean> {
    try {
      const { error } = await supabase.storage
        .from(bucket)
        .remove([path])

      if (error) {
        throw error
      }

      return true
    } catch (error) {
      console.error("Error deleting file:", error);
      return false
    }
  },

  validateFile(file: File): { valid: boolean; error?: string } {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
    const maxSizeBytes = 5 * 1024 * 1024 // 5 MB

    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Only JPEG, PNG, and WebP images are allowed'
      }
    }

    if (file.size > maxSizeBytes) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1)
      return {
        valid: false,
        error: `${file.name} is ${sizeMB}MB — max size is 5MB`
      }
    }

    return { valid: true }
  },

  /**
   * Generate a thumbnail URL using Supabase image transformations
   * @param url - The original image URL
   * @param options - Transformation options
   * @returns The transformed image URL
   */
  getThumbnailUrl(
    url: string,
    options: {
      width?: number
      height?: number
      quality?: number
      resize?: 'cover' | 'contain' | 'fill'
    } = {}
  ): string {
    if (!url) return ''
    
    // Check if this is a Supabase storage URL
    if (!url.includes('supabase.co/storage/')) {
      return url
    }

    const { 
      width = 100, 
      height = 100, 
      quality = 30,
      resize = 'cover' 
    } = options

    // Parse the URL
    const urlObj = new URL(url)
    
    // Check if it's already a render URL (has transformations)
    if (urlObj.pathname.includes('/v1/render/image/')) {
      return url
    }

    // Convert public URL to render URL for transformations
    const transformedPath = urlObj.pathname.replace(
      '/v1/object/public/',
      '/v1/render/image/public/'
    )

    // Build the transformed URL with query parameters
    const transformedUrl = new URL(urlObj.origin + transformedPath)
    transformedUrl.searchParams.set('width', width.toString())
    transformedUrl.searchParams.set('height', height.toString())
    transformedUrl.searchParams.set('quality', quality.toString())
    transformedUrl.searchParams.set('resize', resize)

    return transformedUrl.toString()
  },

  /**
   * Get image URLs with multiple quality presets
   * @param url - The original image URL
   * @returns Object with different quality URLs
   */
  getImageUrls(url: string) {
    if (!url) {
      return {
        thumbnail: '',
        low: '',
        medium: '',
        high: url,
        original: url
      }
    }

    return {
      // Ultra-low quality for initial blur placeholder
      thumbnail: this.getThumbnailUrl(url, {
        width: 50,
        height: 50,
        quality: 20,
        resize: 'cover'
      }),
      // Low quality for fast loading
      low: this.getThumbnailUrl(url, {
        width: 400,
        height: 300,
        quality: 40,
        resize: 'cover'
      }),
      // Medium quality for standard displays
      medium: this.getThumbnailUrl(url, {
        width: 800,
        height: 600,
        quality: 70,
        resize: 'cover'
      }),
      // High quality for large displays
      high: this.getThumbnailUrl(url, {
        width: 1200,
        height: 900,
        quality: 85,
        resize: 'cover'
      }),
      // Original untransformed image
      original: url
    }
  },

  // ── Video upload via TUS resumable protocol (supports files up to 2GB) ─────────

  async uploadClassVideo(
    file: File,
    classId: string,
    videoId: string,
    onProgress?: (progress: number) => void
  ): Promise<{ url: string; path: string; error?: string }> {
    const filePath = `${classId}/${videoId}/${file.name}`;
    const bucketName = 'class-videos';

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { url: '', path: '', error: 'Not authenticated' };
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

    return new Promise((resolve) => {
      const upload = new tus.Upload(file, {
        endpoint: `${supabaseUrl}/storage/v1/upload/resumable`,
        retryDelays: [0, 1000, 3000, 5000],
        chunkSize: 6 * 1024 * 1024, // 6 MB chunks
        headers: {
          authorization: `Bearer ${session.access_token}`,
          'x-upsert': 'false',
        },
        uploadDataDuringCreation: true,
        removeFingerprintOnSuccess: true,
        metadata: {
          bucketName,
          objectName: filePath,
          contentType: file.type,
          cacheControl: '3600',
        },
        onError(error) {
          resolve({ url: '', path: '', error: error.message || 'Upload failed' });
        },
        onProgress(bytesUploaded, bytesTotal) {
          const percentage = Math.round((bytesUploaded / bytesTotal) * 100);
          onProgress?.(percentage);
        },
        onSuccess() {
          const { data: { publicUrl } } = supabase.storage
            .from(bucketName)
            .getPublicUrl(filePath);
          resolve({ url: publicUrl, path: filePath });
        },
      });

      // Check for previous upload to resume
      upload.findPreviousUploads().then((previousUploads) => {
        if (previousUploads.length > 0) {
          upload.resumeFromPreviousUpload(previousUploads[0]);
        }
        upload.start();
      });
    });
  },

  /**
   * Upload a thumbnail image to the class-videos bucket.
   * Uses standard Supabase upload (not tus) since thumbnails are small (~30-50KB).
   */
  async uploadThumbnail(
    blob: Blob,
    classId: string,
    videoId: string,
    index: number
  ): Promise<{ url: string; path: string; error?: string }> {
    const filePath = `${classId}/${videoId}/thumbnails/thumb-${index}.jpg`;
    const bucketName = 'class-videos';

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { url: '', path: '', error: 'Not authenticated' };
    }

    const file = new File([blob], `thumb-${index}.jpg`, { type: 'image/jpeg' });

    const { error } = await supabase.storage
      .from(bucketName)
      .upload(filePath, file, {
        contentType: 'image/jpeg',
        upsert: true,
      });

    if (error) {
      return { url: '', path: '', error: error.message };
    }

    const { data: { publicUrl } } = supabase.storage
      .from(bucketName)
      .getPublicUrl(filePath);

    return { url: publicUrl, path: filePath };
  },

  validateVideoFile(file: File): { valid: boolean; error?: string } {
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/webm'];
    const maxSizeBytes = 2 * 1024 * 1024 * 1024; // 2 GB

    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Only MP4, MOV, and WebM videos are allowed',
      };
    }
    if (file.size > maxSizeBytes) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(0);
      return {
        valid: false,
        error: `${file.name} is ${sizeMB}MB — max size is 2GB`,
      };
    }
    return { valid: true };
  },

  async uploadSongFile(
    file: File,
    classId: string
  ): Promise<{ url: string; path: string; error?: string }> {
    const filePath = `${classId}/sync-music/${file.name}`;
    const bucketName = 'class-videos';

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { url: '', path: '', error: 'Not authenticated' };
    }

    const { error } = await supabase.storage
      .from(bucketName)
      .upload(filePath, file, {
        contentType: file.type,
        upsert: true,
      });

    if (error) {
      return { url: '', path: '', error: error.message };
    }

    const { data: { publicUrl } } = supabase.storage
      .from(bucketName)
      .getPublicUrl(filePath);

    return { url: publicUrl, path: filePath };
  },

  validateSongFile(file: File): { valid: boolean; error?: string } {
    const allowedTypes = ['audio/mpeg', 'audio/wav', 'audio/aac', 'audio/mp4'];
    const maxSizeBytes = 50 * 1024 * 1024; // 50 MB

    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Only MP3, WAV, and AAC audio files are allowed',
      };
    }
    if (file.size > maxSizeBytes) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(0);
      return {
        valid: false,
        error: `${file.name} is ${sizeMB}MB — max size is 50MB`,
      };
    }
    return { valid: true };
  },
}